import { jsPDF } from "jspdf";
import type { NotebookEntry } from "./labNotebook";

const SUB_MAP: Record<string, string> = {
  "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
  "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9",
};
const SUP_MAP: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
  "⁺": "+", "⁻": "-",
};
function ascii(s: string | undefined): string {
  if (!s) return "";
  return s
    .split("")
    .map((c) => SUB_MAP[c] ?? SUP_MAP[c] ?? c)
    .join("")
    .replace(/→/g, "->")
    .replace(/⇌/g, "<->")
    .replace(/·/g, ".")
    .replace(/–/g, "-")
    .replace(/-/g, "-");
}

const stationLabel: Record<NotebookEntry["station"], string> = {
  mixing: "Mixing Station",
  ph: "pH Test",
  flame: "Flame Test",
  titration: "Titration",
};

export function exportNotebookPdf(entries: NotebookEntry[], studentName?: string) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("ElementPad - Lab Notebook", margin, y);
  y += 26;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100);
  if (studentName) {
    doc.text(`Student: ${studentName}`, margin, y);
    y += 14;
  }
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
  y += 14;
  doc.text(`Total experiments: ${entries.length}`, margin, y);
  y += 20;

  // Divider
  doc.setDrawColor(200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;

  if (entries.length === 0) {
    doc.setTextColor(120);
    doc.setFontSize(11);
    doc.text("No experiments recorded yet.", margin, y);
    doc.save("lab-notebook.pdf");
    return;
  }

  // Entries (oldest first reads more like a logbook)
  const ordered = [...entries].sort((a, b) => a.timestamp - b.timestamp);

  ordered.forEach((entry, idx) => {
    ensureSpace(110);

    // Entry header
    doc.setTextColor(0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`Experiment ${idx + 1}: ${ascii(entry.title)}`, margin, y);
    y += 16;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `${stationLabel[entry.station]}  •  ${new Date(entry.timestamp).toLocaleString()}`,
      margin,
      y,
    );
    y += 14;

    doc.setTextColor(0);
    doc.setFontSize(10);

    const writeField = (label: string, value?: string) => {
      if (!value) return;
      const text = ascii(value);
      const lines = doc.splitTextToSize(text, contentWidth - 90);
      ensureSpace(14 * lines.length + 4);
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(lines, margin + 90, y);
      y += 14 * lines.length + 2;
    };

    if (entry.equation) writeField("Equation", entry.equation);
    if (entry.reactants?.length) writeField("Reactants", entry.reactants.join(" + "));
    if (entry.products?.length) writeField("Products", entry.products.join(" + "));
    if (entry.reactionType) writeField("Reaction type", entry.reactionType);
    if (entry.energy) writeField("Energy", entry.energy);
    if (entry.observation) writeField("Observation", entry.observation);
    if (entry.substance) writeField("Substance", entry.substance);
    if (entry.pH !== undefined) writeField("pH", entry.pH.toFixed(2));
    if (entry.metalIon) writeField("Metal ion", entry.metalIon);
    if (entry.flameColor) writeField("Flame colour", entry.flameColor);

    if (entry.titrationData) {
      const t = entry.titrationData;
      writeField("Acid", `${ascii(t.acid)} (${t.concAcid} M, ${t.volumeAcid} mL)`);
      writeField("Titrant", `${ascii(t.base)} (${t.concBase} M)`);
      writeField("Equivalence pt", `${t.equivalenceVolume.toFixed(2)} mL of titrant`);
      writeField("Final reading", `${t.finalVolume.toFixed(2)} mL added → pH ${t.finalPH.toFixed(2)}`);
    }

    if (entry.notes) writeField("Notes", entry.notes);

    y += 8;
    ensureSpace(2);
    doc.setDrawColor(230);
    doc.line(margin, y, pageWidth - margin, y);
    y += 14;
  });

  // Footer page numbers
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `ElementPad Lab Notebook - Page ${i} of ${total}`,
      pageWidth / 2,
      pageHeight - 24,
      { align: "center" },
    );
  }

  doc.save(`lab-notebook-${new Date().toISOString().slice(0, 10)}.pdf`);
}
